import { Dialog, Transition } from '@headlessui/react'
import { VideoCameraIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useState, Fragment, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
// import { FileUploader } from "react-drag-drop-files" // Replaced with custom drag-and-drop
import Loader from '../Loader'
import { getTeamUuid } from '../../utils/cookies'
import { newVideoAnalysis } from '../../Requests/NewAnalysisRequests'
import UpgradedLoader from '../UpgradedLoader'
import SuccessPopup from '../PopupNotifications/SuccessPopup'
import ErrorPopup from '../PopupNotifications/ErrorPopup'
import ChunkedUploader from './ChunkedUploader'
import Cookies from 'universal-cookie'
import axios from 'axios'

// Supported video file types
const fileTypes = ["mp4", "mov", "avi", "mkv", "wmv", "flv", "webm", "m4v", "3gp", "ogv"];

const languageCodes = [
    "pt", "es", "en", "en_au", "en_uk", "en_us", "fr", "de", "it", "nl", "af", "sq", "am",
    "ar", "hy", "as", "az", "ba", "eu", "be", "bn", "bs", "br", "bg", "my", "ca", "zh",
    "hr", "cs", "da", "et", "fo", "fi", "gl", "ka", "el", "gu", "ht", "ha", "haw", "he",
    "hi", "hu", "is", "id", "ja", "jw", "kn", "kk", "km", "ko", "lo", "la", "lv", "ln",
    "lt", "lb", "mk", "mg", "ms", "ml", "mt", "mi", "mr", "mn", "ne", "no", "nn", "oc",
    "pa", "ps", "fa", "pl", "ro", "ru", "sa", "sr", "sn", "sd", "si", "sk", "sl", "so",
    "su", "sw", "sv", "tl", "tg", "ta", "tt", "te", "th", "bo", "tr", "tk", "uk", "ur",
    "uz", "vi", "cy", "yi", "yo"
];

// Get authentication token
function getToken() {
    const cookies = new Cookies();
    const token = cookies.get("token");
    
    // Note: Laravel Sanctum tokens are not JWTs, so we skip format validation
    // Basic XSS protection only
    if (token && (token.includes('<script') || token.includes('javascript:'))) {
        return null;
    }
    
    return token;
}

function UploadVideoFileModal({ handleClose, project }) {
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const { t } = useTranslation()
    const [file, setFile] = useState(null);
    const [step, setStep] = useState(1);
    const [uploading, setUploading] = useState(false)
    const [fileName, setFileName] = useState('')
    const [numberOfSpeakers, setNumberOfSpeakers] = useState('')
    const [language, setLanguage] = useState("pt")
    const [successToast, setSuccessToast] = useState(null)
    const [errorToast, setErrorToast] = useState(null)
    const [useChunkedUpload, setUseChunkedUpload] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [isDragOver, setIsDragOver] = useState(false)
    const abortControllerRef = useRef(null)
    const chunkedUploaderRef = useRef(null)

    const closeModal = () => {
        // Cancel any ongoing uploads
        cancelAllUploads()
        handleClose()
        setOpen(false)
    }

    // Cancel all upload operations
    const cancelAllUploads = () => {
        // Cancel regular upload if in progress
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
            setUploading(false)
        }
        
        // Cancel chunked upload if in progress
        if (chunkedUploaderRef.current && chunkedUploaderRef.current.cancelUpload) {
            chunkedUploaderRef.current.cancelUpload()
        }
    }

    // Helper function to validate authentication token
    const validateToken = () => {
        const cookies = new Cookies();
        const token = cookies.get("token");
        
        if (!token) {
            console.error('No authentication token found');
            setErrorToast('Authentication token missing. Please log in again.');
            return false;
        }
        
        // Token validation check passed
        return true;
    };

    // Helper function to handle regular upload with retry logic
    const startRegularUpload = async (selectedFile, retryCount = 0) => {
        const maxRetries = 3;
        setUploading(true);
        
        // Validate token before making request
        if (!validateToken()) {
            setUploading(false);
            setStep(1); // Go back to configuration
            return;
        }
        
        try {
            // Create abort controller for this upload
            abortControllerRef.current = new AbortController();
            
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('project_uuid', project.uuid);
            formData.append('name', fileName || selectedFile.name);
            formData.append('language', language);
            if (numberOfSpeakers) {
                formData.append('number_of_speakers', numberOfSpeakers);
            }
            formData.append('team_uuid', getTeamUuid());
            
            // Use longer timeout for large files (10 minutes for files over 1GB, otherwise 5 minutes)
            const timeout = selectedFile.size > (1024 * 1024 * 1024) ? 600000 : 300000; // 10min or 5min
            const response = await axios.post(
                process.env.REACT_APP_API_BASE_URL + "/video_analysis/create",
                formData,
                { 
                    headers: { authorization: `Bearer ${getToken()}` },
                    timeout: timeout,
                    signal: abortControllerRef.current.signal
                }
            );
            
            // Handle successful response
            window.location.reload(false);
            setUploading(false);
            
        } catch (error) {
            console.error('Upload error:', error);
            
            // Check if upload was aborted (modal closed)
            if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
                // Upload cancelled by user
                setUploading(false);
                return; // Don't show error message for intentional cancellation
            }
            
            // Check if it's a 429 rate limiting error
            if (error.response && error.response.status === 429) {
                if (retryCount < maxRetries) {
                    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
                    setErrorToast(`Server is busy. Retrying in ${retryDelay / 1000} seconds... (Attempt ${retryCount + 1}/${maxRetries})`);
                    
                    setTimeout(async () => {
                        await startRegularUpload(selectedFile, retryCount + 1);
                    }, retryDelay);
                    return;
                } else {
                    setErrorToast('Server is too busy right now. Please try uploading your video in a few minutes.');
                }
            } else if (error.response && error.response.status === 413) {
                console.error('413 Content Too Large during upload:', {
                    fileSize: selectedFile.size,
                    fileSizeMB: (selectedFile.size / (1024 * 1024)).toFixed(2),
                    status: error.response.status,
                    statusText: error.response.statusText
                });
                setErrorToast(
                    `File too large for server (413 error). ` +
                    `Your file: ${(selectedFile.size / (1024 * 1024)).toFixed(0)}MB. ` +
                    `Server needs configuration update: nginx client_max_body_size, PHP upload_max_filesize, and post_max_size. ` +
                    `Please contact your system administrator.`
                );
            } else if (error.response && error.response.status === 302) {
                console.error('302 Redirect during regular upload:', {
                    redirectLocation: error.response.headers?.location,
                    status: error.response.status,
                    statusText: error.response.statusText,
                    config: error.config
                });
                setErrorToast('Redirect error during upload. This usually indicates authentication issues or wrong endpoint URL. Please try logging in again.');
            } else if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                setErrorToast('Authentication error. Please check your login status and try again.');
            } else if (error.response && error.response.status >= 500) {
                // Server error - also worth retrying
                if (retryCount < maxRetries) {
                    const retryDelay = Math.pow(2, retryCount) * 1000;
                    setErrorToast(`Server error. Retrying in ${retryDelay / 1000} seconds... (Attempt ${retryCount + 1}/${maxRetries})`);
                    
                    setTimeout(async () => {
                        await startRegularUpload(selectedFile, retryCount + 1);
                    }, retryDelay);
                    return;
                } else {
                    setErrorToast('Server error. Please try again later.');
                }
            } else {
                // Other errors (network, validation, etc.)
                setErrorToast(error.response?.data?.message || t('VideoSessions.Toast.UploadVideoFailed'));
            }
            
            setUploading(false);
            setStep(2); // Go back to file selection
        }
    };

    // Handle file selection for traditional upload
    const handleChange = async (selectedFile) => {
        try {
            // Verify the file object is valid
            if (!selectedFile || !selectedFile.size) {
                setErrorToast('Invalid file selected');
                return;
            }

            setFile(selectedFile);
            
            // Check file size to determine upload method
            const fileSize = selectedFile.size;
            const chunkUploadThreshold = 100 * 1024 * 1024; // 100MB - use chunked upload for files over this
            const maxUploadSize = 10 * 1024 * 1024 * 1024; // 10GB - absolute maximum
            
            // Check if file exceeds maximum size
            if (fileSize > maxUploadSize) {
                setErrorToast(`File is too large. Maximum supported size is 10GB. Your file is ${(fileSize / (1024 * 1024 * 1024)).toFixed(2)}GB.`);
                return;
            }
            
            // Determine upload method
            if (fileSize > chunkUploadThreshold) {
                setUseChunkedUpload(true);
            } else {
                setUseChunkedUpload(false);
            }
            
            // Move to step 3 and start appropriate upload method immediately
            setStep(3);
            
            // Start upload based on method - automatically without user confirmation
            if (fileSize <= chunkUploadThreshold) {
                await startRegularUpload(selectedFile);
            }
            // For chunked uploads, the ChunkedUploader component will handle the upload automatically
            
        } catch (error) {
            setErrorToast(`Error processing file: ${error.message}`);
        }
    };

    // Handle chunked upload completion
    const handleChunkedUploadComplete = (analysisUuid) => {
        setSuccessToast('Video uploaded successfully! Processing will begin shortly.');
        setTimeout(() => {
            window.location.reload(false);
        }, 2000);
    };

    // Handle chunked upload error
    const handleChunkedUploadError = (error) => {
        setErrorToast(error);
        setUploading(false);
    };

    // Handle chunked upload progress
    const handleChunkedUploadProgress = (progress) => {
        setUploadProgress(progress);
    };

    // Validate file type
    const isValidFileType = (file) => {
        const extension = file.name.split('.').pop().toLowerCase();
        return fileTypes.includes(extension);
    };

    // Handle file input change (click to upload)
    const handleFileInputChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            if (isValidFileType(selectedFile)) {
                handleChange(selectedFile);
            } else {
                setErrorToast(`${t('VideoUpload.InvalidFileType')}. ${t('VideoUpload.SupportedTypes')}: ${fileTypes.join(', ')}`);
            }
        }
    };

    // Handle drag and drop events
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleFileDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        
        if (files.length > 0) {
            const droppedFile = files[0];
            
            // Check if file size seems reasonable (not truncated)
            if (droppedFile.size === 0) {
                setErrorToast('File appears to be empty or could not be read properly.');
                return;
            }
            
            if (isValidFileType(droppedFile)) {
                handleChange(droppedFile);
            } else {
                setErrorToast(`${t('VideoUpload.InvalidFileType')}. ${t('VideoUpload.SupportedTypes')}: ${fileTypes.join(', ')}`);
            }
        }
    };

    const handleSelectChange = (event) => {
        setLanguage(event.target.value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formFileName = e.target.file_name.value || `Video Upload ${new Date().toLocaleString()}`;
        const formSpeakers = e.target.number_of_speakers.value || '';
        
        setFileName(formFileName);
        if (formSpeakers) {
            setNumberOfSpeakers(formSpeakers)
        }
        setStep(2); // Move to file selection step
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={() => closeModal()}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex w-full min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 h-full">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900 text-center"
                                >
                                    {t('Audio.VideoUpload.Title')}
                                </Dialog.Title>
                                
                                {step == 3 && (
                                    <div className='flex w-full h-full flex-col justify-evenly rounded-3xl py-8'>
                                        {useChunkedUpload && file ? (
                                            <ChunkedUploader
                                                ref={chunkedUploaderRef}
                                                file={file}
                                                fileName={fileName || file.name}
                                                metadata={{
                                                    name: fileName || file.name,
                                                    language: language,
                                                    number_of_speakers: numberOfSpeakers || null
                                                }}
                                                projectUuid={project.uuid}
                                                teamUuid={getTeamUuid()}
                                                onProgress={handleChunkedUploadProgress}
                                                onComplete={handleChunkedUploadComplete}
                                                onError={handleChunkedUploadError}
                                            />
                                        ) : (
                                            <div className="h-full min-h-full flex flex-col flex-grow gap-8 text-center justify-evenly">
                                                <UpgradedLoader className="animate-pulse" w={8} h={8} bg="bg-black" />
                                                <div className='text-sm'>{t('Audio.VideoUpload.UploadMessage')}</div>
                                                {uploadProgress > 0 && (
                                                    <div className="w-full max-w-md mx-auto">
                                                        <div className="text-sm text-gray-600 mb-2">{Math.round(uploadProgress)}%</div>
                                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                                            <div 
                                                                className="bg-black h-2 rounded-full transition-all duration-300"
                                                                style={{ width: `${uploadProgress}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {step == 2 && (
                                    <div className='flex w-full h-full flex-col justify-evenly rounded-3xl py-8'>
                                        <div>
                                            {/* Custom drag and drop area for large file support */}
                                            <div 
                                                className="col-span-full"
                                                onDrop={handleFileDrop}
                                                onDragOver={handleDragOver}
                                                onDragEnter={handleDragEnter}
                                                onDragLeave={handleDragLeave}
                                            >
                                                <div className={`mt-2 flex justify-center rounded-lg border border-dashed px-6 py-10 transition-colors ${
                                                    isDragOver 
                                                        ? 'border-black bg-gray-50' 
                                                        : 'border-gray-900/25 hover:border-gray-400'
                                                }`}>
                                                    <div className="text-center">
                                                        <VideoCameraIcon className="mx-auto h-12 w-12 text-black" aria-hidden="true" />
                                                        <div className="mt-4 flex justify-center text-sm leading-6 text-gray-600">
                                                            <label
                                                                htmlFor="file-upload-step2"
                                                                className="relative cursor-pointer rounded-md bg-black font-semibold text-white px-2 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 hover:text-black"
                                                            >
                                                                <span>{t('Audio.VideoUpload.Step1.ModalTitle')}</span>
                                                                <input 
                                                                    id="file-upload-step2" 
                                                                    name="file-upload-step2" 
                                                                    type="file" 
                                                                    className="sr-only"
                                                                    accept={fileTypes.map(type => `.${type}`).join(',')}
                                                                    onChange={handleFileInputChange}
                                                                />
                                                            </label>
                                                            <p className="pl-1">{t('Audio.VideoUpload.Step1.ModalDescription')}</p>
                                                        </div>
                                                        <p className="text-xs leading-5 text-gray-600">{t('Audio.VideoUpload.Step1.ModalFileTypes')}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                    </div>
                                )}
                                
                                {step == 1 && (
                                    <form className='flex w-full h-full flex-col justify-between gap-8' onSubmit={handleSubmit}>
                                        <div>
                                            <label htmlFor="file_name" className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Audio.VideoUpload.Step2.VideoName')}
                                            </label>
                                            <div className='flex w-full flex-col mt-2 items-center'>
                                                <input
                                                    type="text"
                                                    name="file_name"
                                                    id="file_name"
                                                    className="block w-full text-center rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-black focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                                                    placeholder={t('Audio.VideoUpload.Step2.VideoNamePlaceholder') + new Date().toLocaleString()}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label htmlFor="language" className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Audio.VideoUpload.Step2.LanguageLabel')}
                                            </label>
                                            <select
                                                id="language"
                                                name="language"
                                                className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-black sm:text-sm sm:leading-6"
                                                defaultValue="pt"
                                                onChange={handleSelectChange}
                                            >
                                                {languageCodes.map((code) => (
                                                    <option key={code} value={code}>
                                                        {t(`languages.${code}`) || code.toUpperCase()}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div>
                                            <label htmlFor="number_of_speakers" className="block text-sm font-medium leading-6 text-gray-900">
                                                {t('Audio.VideoUpload.Step2.SpeakersLabel')}
                                            </label>
                                            <div className='flex w-full flex-col mt-2 items-center'>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={20}
                                                    name="number_of_speakers"
                                                    id="number_of_speakers"
                                                    className="block w-full text-center rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-black focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                                                    placeholder={t('Audio.VideoUpload.Step2.SpeakersPlaceholder')}
                                                />
                                            </div>
                                        </div>

                                        <div className='flex flex-col sm:flex-row gap-2 w-full justify-center items-center mt-8'>
                                            {uploading ? (
                                                <button
                                                    type='button'
                                                    disabled
                                                    className='bg-black text-white rounded-md px-12 py-2 w-fit'
                                                >
                                                    <Loader />
                                                </button>
                                            ) : (
                                                <button
                                                    type='submit'
                                                    className='bg-black text-white rounded-md px-12 py-2 w-fit'
                                                >
{t('Audio.VideoUpload.Step2.NextButton')}
                                                </button>
                                            )}
                                        </div>
                                    </form>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
            
            {/* Toast Notifications */}
            {successToast && (
                <SuccessPopup
                    text={successToast}
                    handleClose={() => setSuccessToast(null)}
                />
            )}
            {errorToast && (
                <ErrorPopup
                    text={errorToast}
                    handleClose={() => setErrorToast(null)}
                />
            )}
        </Transition.Root>
    )
}

export default UploadVideoFileModal